import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {currencyFormat, dateFormat} from '../../../utils/functions/Functions';
import DownloadPdfService from '../../../Services/DownloadPdfService/DownloadPdfService';
import './ApprovedOfferInfo.scss';
import {
  updateToggleDealerConfirmationDialog,
  updateToggleDealerConnectDialog,
  updateToggleDealerSubmissionDialog,
  updateTotalApprovedAmount
} from '../../../redux/app-state-slice';
import DealerConnect from '../../../components/DealerConnect/DealerConnect';
import DealerSubmission from '../../../components/DealerSubmission/DealerSubmission';
import DealerConfirmation from '../../../components/DealerConfirmation/DealerConfirmation';
import {
  showSurvey,
  tagConnectWithADealerClick,
  tagDownloadPrequalifiedOffer,
  tagExploreVehicleOnlineClick,
  tagPrequalifiedResultPage
} from '../../../Services/TaggingService/TaggingService';
import TimeOutSession from '../../../components/TimeOutSession/TimeOutSession';
import CheckIcon from '../../../assets/icons/outline-circle-checkmark-green-thin.svg';
import FordCreditLogo from '../../../assets/icons/ford-credit-logo.png';
import {closeTier3ModalEvent} from '../../../utils/tier-3-modal-close/PrequalCloseEvent';
import {trackEvents} from '../../../Services/OptimizelyService/OptimizelyService';
import {getQueryElement} from '../../../utils/queryElement';
import DealerModalDialog from '../../../components/DealerModalDialog/DealerModalDialog';
import Button from '../../../components/Button/Button';
import {AccordionPanel} from '../../../components/Accordion/AccordionPanel';
import {createPdf} from '../../../utils/constants/EndPoint';
import {submitDealerSearch} from '../../../Services/SubmitDealerSearchService/SubmitDealerSearchService';
import SpecialEventApprovedSection from '../../../components/SpecialEvent/SpecialEventApprovedSection/SpecialEventApprovedSection';
import {SPECIAL_EVENT_TARGET} from '../../../utils/constants/constants';
import {FormHeaderSection} from '../../PrequalSinglePageApp/FormHeaderSection/FormHeaderSection';

const faqDropDownValues = [
  {
    idx: '1',
    title: "Why don't I see my APR or Payment?",
    content:
      "We tailor your APR and payments to the vehicle you want. When you shop online or with a dealer, you'll be able to see real rates and payments on your preferred vehicles.",
    border: true
  },
  {
    idx: '2',
    title: "Why doesn't prequalification affect my credit score?",
    content:
      "Because you're not applying for credit, it will not affect your credit score. Prequalification requires you to report your income and personal information, and then a soft credit inquiry is made.",
    border: true
  },
  {
    idx: '3',
    title: 'Can I get prequalified more than once?',
    content: 'Yes. Results are valid for 180 days. You may get prequalified again after that time.'
  }
];

export async function downloadOffer(result, appConfig, customerData) {
  const searchCriteria = {
    searchString: customerData.tier3PACode?.value ? customerData.tier3PACode.value : null,
    searchType: 'DETAILS'
  };
  const dealerDetails = await submitDealerSearch(searchCriteria, appConfig);
  const isFromDealerSite = customerData.isDealerHostApplication.value || customerData.prequalSource.value === 'qr-WC';
  const pdfData = {
    firstName: customerData.firstName.value.trim(),
    totalApprovedAmount: currencyFormat(result.totalApprovedAmount),
    expirationDate: result.expirationDate,
    effectiveDate: result.effectiveDate,
    offerId: result.offerId,
    isFromDealerSite,
    dealerInfo: isFromDealerSite ? dealerDetails.dealers[0] : null,
    dealerId: customerData.tier3PACode?.value ? customerData.tier3PACode.value : null,
    countryCode: customerData.countryCode.value,
    stateOrProvince: customerData.stateOrProvince.value
  };
  const pdfFileName = customerData.languageCode === 'FR' ? 'Offre_Prequalification.pdf' : 'PreQualification_Offer.pdf';
  await DownloadPdfService.downloadPdfOffer(appConfig.exrServiceApi + createPdf, pdfFileName, pdfData, customerData);
}

function ApprovedOfferInfo({result, customerData, appConfig, dispatch}) {
  const {totalApprovedAmount} = result;
  const {expirationDate} = result;
  const {offerId} = result;

  const onClickDownload = (e) => {
    e.preventDefault();
    tagDownloadPrequalifiedOffer();
    downloadOffer(result, appConfig, customerData);
  };

  const redirectToTarget = (url) => {
    if (url === appConfig.shopTarget) {
      tagExploreVehicleOnlineClick();
    }
    window.open(url, '_blank');
  };

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(updateTotalApprovedAmount(result.totalApprovedAmount));
    if (!customerData.isDealerHostApplication.value) {
      const queryElement = getQueryElement(customerData);
      queryElement.getElementById('prequal--web--app').scrollIntoView();
    }
    tagPrequalifiedResultPage(offerId, customerData);
    showSurvey(customerData.email.value);
    if (customerData.prequalSource.value === 'web' && customerData?.recaptchaOptimizelyData?.isEnabled) {
      trackEvents('Decision Page View', customerData);
    }
  }, []);

  const termsAndConditionsLink = (url) => {
    window.open(url, '_blank');
  };

  const connectToDealerProps = {};
  connectToDealerProps.title = 'Connect with a Ford Dealer';
  connectToDealerProps.confirmText = '';
  connectToDealerProps.closeDialog = () => {
    dispatch(updateToggleDealerConnectDialog(false));
  };
  connectToDealerProps.content = () => (
    <div className='prequal--web--dealer-search-padding-fomo'>
      <DealerConnect dispatch={dispatch} customerData={customerData} appConfig={appConfig} hasSelectButton />
    </div>
  );
  const showDealerConnectDialog = () => {
    tagConnectWithADealerClick(customerData.specialEvent.value);
    dispatch(updateToggleDealerConnectDialog(true));
  };

  const dealerSubmissionProps = {};
  dealerSubmissionProps.title = 'Connect with a Ford Dealer';
  dealerSubmissionProps.confirmText = 'Submit Inquiry';
  dealerSubmissionProps.closeDialog = () => {
    dispatch(updateToggleDealerSubmissionDialog(false));
  };

  dealerSubmissionProps.confirmDialog = () => {
    dispatch(updateToggleDealerSubmissionDialog(false));
  };
  dealerSubmissionProps.content = () => <DealerSubmission dispatch={dispatch} customerData={customerData} appConfig={appConfig} />;

  const dealerConfirmationProps = {};
  dealerConfirmationProps.title = 'Connect with a Ford Dealer';
  dealerConfirmationProps.confirmText = 'Return to Prequalification Offer';
  dealerConfirmationProps.closeDialog = () => {
    dispatch(updateToggleDealerConfirmationDialog(false));
  };

  dealerConfirmationProps.content = () => <DealerConfirmation dispatch={dispatch} customerData={customerData} appConfig={appConfig} />;

  function closeEvent() {
    closeTier3ModalEvent({}, dispatch, customerData);
  }

  const getDownloadIcon = (isHover) => (
    <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        fill-rule='evenodd'
        clip-rule='evenodd'
        d='M10.8601 3.59998V10.86H8.4601L12.0601 17.16L15.6601 10.86H13.2601V3.59998H10.8601ZM19.3801 19.32V16.92H20.5201V20.52H3.6001V16.92H4.8601V19.32H19.3801Z'
        fill={isHover ? '#044EA7' : '#066FEF'}
      />
    </svg>
  );
  return (
    <>
      {customerData.isDealerHostApplication.value ? (
        <>
          <FormHeaderSection
            isDealerHostApplication
            dispatch={dispatch}
            customerData={customerData}
            navigate={navigate}
            headerText='Prequalify for financing in no time at all.'
          />
          <div data-testid='offer-container'>
            <div className='prequal--web--offer-content prequal--wc--offer-content'>
              <div className='prequal--wc--offer-congratulation-container'>
                <div className='prequal--wc--offer-icon-container'>
                  <img alt='' src={CheckIcon} width='38' height='38' />
                </div>
                <div className='prequal--wc--offer-text-container'>
                  <span data-cs-mask='true' className='prequal--wc--offer-customer-name'>
                    Congrats, {customerData?.firstName?.value.trim()}!
                  </span>
                  <span className='prequal--wc--prequalified-text'>You’re prequalified for:</span>
                </div>
              </div>
              <div className='prequal--wc--offer-details-container'>
                <div className='prequal--wc--offer-details-upto'>Up to</div>
                <div className='prequal--wc--offer-details-amount'>{totalApprovedAmount && currencyFormat(totalApprovedAmount)}</div>
                <div className='prequal--wc--offer-details-expiration'>
                  <span>Offer Expiration Date:</span>
                  <span className='prequal--wc--offer-details-expiration-date'>{expirationDate && dateFormat(expirationDate)}</span>
                </div>
                <div className='prequal--wc--offer-details-fordlogo'>
                  <img className='prequal--wc--ford-credit-logo-dealer-offer' alt='' src={FordCreditLogo} />
                </div>
              </div>

              <div className='prequal--wc--download-offer-button-icon-container'>
                <Button
                  label='Download Your Offer'
                  dataTestId='download-your-offer'
                  buttonStyle='tertiary'
                  onClick={(e) => onClickDownload(e)}
                  leftIcon={getDownloadIcon}
                />
              </div>

              <div className='prequal--wc--offer-terms'>
                <div>
                  *Your pre-qualification amount is an estimate and was determined by the information you provided online. See your dealer
                  to discuss your financing options. See our{' '}
                  <a
                    href='./terms-and-conditions'
                    className='prequal--web--terms-and-conditions-link'
                    onClick={(e) => {
                      e.preventDefault();
                      termsAndConditionsLink('./terms-and-conditions');
                    }}
                  >
                    Terms and Conditions
                  </a>
                  .
                </div>
              </div>
              <div className='prequal--wc--offer-close'>
                <button
                  type='button'
                  className='prequal--web--submit-prequalification-button prequal--wc--offer-close-button'
                  onClick={closeEvent}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div id='approved-offer-container' className='prequal--web--offer-main-container'>
            <div hidden data-testid='offer-id'>
              {result?.offerId}
            </div>
            <div className='prequal--web--offer-congrats-outer-container'>
              <img className='prequal--web--offer-details-checkCircle' alt='' src={CheckIcon} />
              <div className='prequal--web--offer-congrats-container'>
                <span data-cs-mask='true' className='prequal--wc--offer-approved-customer-name'>
                  Congrats, {customerData?.firstName?.value.trim()}!
                </span>
                <span className='prequal--wc--approved-prequalified-text'>You’re prequalified for:</span>
              </div>
            </div>
            <div className='prequal--web--offer-details-parent-container'>
              <div className='prequal--web--offer-details-outer-container'>
                <div className='prequal--new--offer-details-container'>
                  <div className='prequal--wc--offer-details-upto'>Up to</div>
                  <div className='prequal--wc--offer-details-amount'>{totalApprovedAmount && currencyFormat(totalApprovedAmount)}</div>
                  <div className='prequal--wc--offer-details-expiration'>
                    <span>Offer Expiration Date:</span>
                    <span className='prequal--wc--offer-details-expiration-date'>{expirationDate && dateFormat(expirationDate)}</span>
                  </div>
                  <div className='prequal--wc--offer-details-fordlogo'>
                    <img className='prequal--wc--ford-credit-logo-dealer-offer' alt='' src={FordCreditLogo} />
                  </div>
                </div>
                {customerData.specialEvent.value ? (
                  <SpecialEventApprovedSection customerData={customerData} dispatch={dispatch} />
                ) : (
                  <div className='prequal--wc--offer-nextstep-container'>
                    <span className='prequal--wc--offer-nextstep'>Take The Next Step</span>
                    <span className='prequal--wc--dealermsg-text-fomo'>
                      {customerData.countryCode.value === 'CAN'
                        ? 'Download your offer and share with your local dealer'
                        : 'Send this prequalification to your local dealer to continue to your next Ford.'}
                    </span>
                    <Button
                      onClick={(e) => {
                        if (customerData.countryCode.value === 'CAN') {
                          onClickDownload(e);
                        } else {
                          showDealerConnectDialog();
                        }
                      }}
                      dataTestId='connect-with-dealer-button'
                      label={customerData.countryCode.value === 'CAN' ? 'Download Offer' : 'Send To Dealer'}
                      buttonStyle='secondary'
                    />
                  </div>
                )}
              </div>
            </div>
            <div className='prequal--wc--offer-details-email'>Your prequalification offer has been sent to your email.</div>

            <div className='prequal--wc--download-offer-button-icon-container'>
              <Button
                label={customerData.countryCode.value === 'CAN' ? 'Download Offer' : 'Download Your Offer'}
                dataTestId='download-your-offer'
                buttonStyle='tertiary'
                onClick={(e) => onClickDownload(e)}
                leftIcon={getDownloadIcon}
              />
            </div>
            <div className='prequal--wc--offer-details-tc'>
              *Your prequalification amount is an estimate and was determined by the information you provided online. See your dealer to
              discuss your financing options. See our{' '}
              <a
                href='./terms-and-conditions'
                className='prequal--web--terms-and-conditions-link'
                onClick={(e) => {
                  e.preventDefault();
                  termsAndConditionsLink('./terms-and-conditions');
                }}
              >
                Terms and Conditions
              </a>
              .
            </div>
            <div className='prequal--web--explore-section-title-container'>
              <h2>Let’s Explore Your Options</h2>
              <h3>Here’s how you can use your prequalification.</h3>
            </div>
            <div className='prequal--web--explore-section-card-container'>
              <div className='prequal--web--explore-section-card'>
                <div id='prequal--web--explore-apply-image' />
                <div className='prequal--web--explore-section-card-content'>
                  <div>
                    <h4>Apply For Credit</h4>
                    <p>Save time at the dealership by sharing your prequalification offer now.</p>
                  </div>
                  <Button
                    onClick={() => {
                      redirectToTarget(appConfig.ocaTarget);
                    }}
                    dataTestId='get-qualified-button'
                    label='Apply'
                    buttonStyle='secondary'
                  />
                </div>
              </div>
              <div className='prequal--web--explore-section-card'>
                <div id='prequal--web--explore-vehicle-image' />
                <div className='prequal--web--explore-section-card-content'>
                  <div>
                    <h4>Explore Vehicles</h4>
                    <p>Shop with confidence and explore vehicles that fit in your budget.</p>
                  </div>
                  <Button
                    onClick={() => {
                      redirectToTarget(customerData.specialEvent.value ? SPECIAL_EVENT_TARGET : appConfig.shopTarget);
                    }}
                    dataTestId='explore-vehicles-button'
                    label='Explore'
                    buttonStyle='secondary'
                  />
                </div>
              </div>
            </div>

            <div className='prequal--web--approval-faq-container'>
              <div className='prequal--web--blue-curved-bg' />
              <div className='prequal--web--approval-faq-content-container'>
                <div className='prequal--web--approval-faq-content'>
                  <AccordionPanel accordionDropDownValues={faqDropDownValues} accordionTitle='Frequently Asked Questions' />
                </div>
              </div>
            </div>
            <div />
            <div>
              {customerData.toggleDealerConnectDialog.value && (
                <DealerModalDialog props={connectToDealerProps} showDialogWithHeader appConfig={appConfig} />
              )}
            </div>
            <div>
              {customerData.toggleDealerSubmissionDialog.value && (
                <DealerModalDialog props={dealerSubmissionProps} showDialogWithHeader appConfig={appConfig} />
              )}
            </div>
            <div>
              {customerData.toggleDealerConfirmationPage.value && (
                <DealerModalDialog props={dealerConfirmationProps} showDialogWithHeader appConfig={appConfig} />
              )}
            </div>
          </div>
          <TimeOutSession appConfig={appConfig} dispatch={dispatch} customerData={customerData} navigate={navigate} />
        </>
      )}
    </>
  );
}

export default ApprovedOfferInfo;
